import './Spinner.css';

/**
 * Spinner — loading indicator.
 *
 * `decorative` (consistency sweep, 2026-07-14; default false): a Spinner
 * nested INSIDE a control that already carries its own pending-state
 * accessible name (e.g. a submit button whose text becomes "Saving…") would,
 * with the default role="status" + aria-label="Loading", fold "Loading" into
 * that control's accessible name (name-from-content) — a screen reader can
 * double-announce something like "Loading Saving…" instead of just
 * "Saving…". `decorative` drops the role/aria-label and marks the icon
 * aria-hidden so it's purely visual; the parent control's own accessible
 * name (or an ancestor live region) stays the single source of truth.
 *
 * Use `decorative` for in-button/inline pending indicators (the control
 * itself already says "Saving…"/"Sending…"). Leave it false (the default)
 * for a standalone loading state with no other accessible name of its own —
 * e.g. a bare full-page/section loader.
 *
 * COLOR (PR #463 review M1, 2026-07-14): `decorative` also adds a
 * `spinner--decorative` modifier class that switches the ring to
 * `currentColor` (Button's own `.btn__spinner` idiom — a solid ring with a
 * transparent top notch) instead of the standalone green-arc/cream-ring token
 * pair. In-button spinners sit on whatever the button's own text color is
 * (e.g. white on the gradient CTAs), where the fixed token pair read as
 * green-on-green. Standalone (non-decorative) spinners are unaffected and
 * keep the token colors.
 */
export default function Spinner({ size = 'md', className = '', decorative = false }) {
  const a11yProps = decorative
    ? { 'aria-hidden': 'true' }
    : { role: 'status', 'aria-label': 'Loading' };
  return (
    <div
      className={`spinner spinner--${size}${decorative ? ' spinner--decorative' : ''} ${className}`}
      {...a11yProps}
    >
      <span className="spinner__circle" />
    </div>
  );
}
